import NoImageSrc from "@/assets/images/no_image.svg";
import LazyLoadedVideo from "@/components/common/LazyLoadedVideo";
import { CLink } from "@/components/common/customLink/CustomLink";
import { cn } from "@/lib/tailwind";
import { useNavContext } from "@/providers/NavProvider";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { getLangFromLocale } from "@/utils/common";
import { AnimatePresence, motion } from "framer-motion";
// import Image from "next/image";
import Image from "@/components/ui/imageV2/ImageV2";
import { useParams } from "next/navigation";
import { createPortal } from "react-dom";

interface IProps {
  showMenu?: boolean;
  idx: number;
}

const Element = ({ showMenu, idx }: IProps) => {
  // hooks
  const { isScrolling, animation, duration, dynamicHeader, navData } = useNavContext();
  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);

  // consts
  const menuData = navData.menu.menu[idx];
  const subMenu = menuData.subMenu ?? [];

  // methods
  function handlePosition() {
    if (dynamicHeader) {
      if (isScrolling) return "md:top-[129px]";
      return "md:top-[189px]";
    } else {
      if (isScrolling) return "md:top-[79px]";
      return "md:top-[139px]";
    }
  }

  function handleGetPromotionalTiles() {
    if (menuData.promotionalTilesLayout === "single")
      return menuData.promotionalTiles?.map((tile, idx) => (
        <div key={idx} className="col-span-4 col-start-11 flex flex-col gap-4">
          <p className="font-semibold">
            {lang === "en" ? menuData.columnTitle?.en : menuData.columnTitle?.ar}
          </p>
          <CLink href={tile.link?.url ?? "/"}>
            {tile.mediaType === "image" ? (
              <Image
                src={
                  lang === "en"
                    ? (getCloudinaryURL(tile.media.en) ?? NoImageSrc)
                    : (getCloudinaryURL(tile.media.ar) ?? NoImageSrc)
                }
                alt="nav image"
                className="w-full"
                width={432}
                height={300}
              />
            ) : (
              <></>
            )}
            {tile.mediaType === "video" ? (
              <div style={{ aspectRatio: "0.88/1" }}>
                <LazyLoadedVideo
                  controls={false}
                  width="100%"
                  height="100%"
                  src={lang == "en" ? tile.media?.en : tile.media?.ar}
                />
              </div>
            ) : (
              <></>
            )}
          </CLink>
          <CLink href={tile.link?.url ?? "/"}>
            {lang === "en" ? tile.linkText?.en : tile.linkText?.ar}
          </CLink>
        </div>
      ));
    if (menuData.promotionalTilesLayout === "double")
      return (
        <div className="col-span-4 col-start-11 flex flex-col gap-4">
          <p className="font-semibold">
            {lang === "en" ? menuData.columnTitle?.en : menuData.columnTitle?.ar}
          </p>
          {menuData.promotionalTiles?.map((tile, idx) => (
            <div key={idx} className="flex flex-col gap-3">
              <CLink href={tile.link?.url ?? "/"}>
                {tile.mediaType === "image" ? (
                  <Image
                    src={
                      lang === "en"
                        ? tile.media.en
                          ? getCloudinaryURL(tile.media.en)
                          : NoImageSrc
                        : tile.media.ar
                          ? getCloudinaryURL(tile.media.ar)
                          : NoImageSrc
                    }
                    alt="nav image"
                    className="w-full"
                    width={432}
                    height={100}
                  />
                ) : (
                  <></>
                )}
                {tile.mediaType === "video" ? (
                  <div style={{ aspectRatio: "2.17/1" }}>
                    <LazyLoadedVideo
                      controls={false}
                      width="100%"
                      height="100%"
                      src={lang == "en" ? tile.media?.en : tile.media?.ar}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </CLink>
              <CLink href={tile.link?.url ?? "/"}>
                {lang === "en" ? tile.linkText?.en : tile.linkText?.ar}
              </CLink>
            </div>
          ))}
        </div>
      );
  }

  return (
    <AnimatePresence>
      {showMenu && (
        <motion.div
          className={cn(
            "fixed left-1/2 z-30 hidden w-full -translate-x-1/2 bg-white text-black shadow-md transition-[top] md:block",
            animation,
            duration,
            handlePosition(),
          )}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <div className="container px-4 md:px-12">
            <div
              className={cn(
                "min-h-max border-t pb-8 pt-12 transition-[border]",
                isScrolling ? "border-transparent" : "border-black",
                animation,
                duration,
              )}
            >
              <div className="grid h-full grid-cols-14 gap-x-4">
                {subMenu.map((m, i) => (
                  <div key={i} className="col-span-2 flex flex-col gap-3">
                    <p className="font-semibold">{lang === "en" ? m.title.en : m.title.ar}</p>
                    {m.subMenu?.map((sm, j) => (
                      <CLink key={i + j} href={sm.link?.url ?? "/"} className="block text-sm">
                        {lang === "en" ? sm.title.en : sm.title.ar}
                      </CLink>
                    ))}
                  </div>
                ))}
                <>{handleGetPromotionalTiles()}</>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const NavDesktopDropDown = ({ showMenu, idx }: IProps) => {
  if (typeof window !== "object") return;
  return createPortal(<Element showMenu={showMenu} idx={idx} />, document.querySelector("body")!);
};
